import { useReducer } from "react";
import AisleContext, { internalState } from "./context";
import reducer from "./reducer";
import { execute } from "../../../utils/helpers/execute";
import EndPoints from "../../../services/end-points";
import { IAisle, IAisleDetails } from "../../../models/wms/aisle/response";
import { ICreateAisle, IUpdateAisle } from "../../../models/wms/aisle/request";
import { IAisleQuery } from "../../../models/wms/aisle/query";

export interface IProps {
  children: React.ReactNode;
}

const AisleContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.wmsLayout.getAllAisles(state.query);

        dispatch({ type: "SET_LIST", payload: { list: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDetails = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });

        const { data } = await EndPoints.wmsLayout.getAisle(id);
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
      },
      throwException: false,
    });
  };

  const setDetails = async (data?: IAisleDetails) => {
    dispatch({ type: "SET_DETAILS", payload: { details: data } });
  };

  const createAisle = async (request: ICreateAisle) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });

        await EndPoints.wmsLayout.createAisle(request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "create" } });
      },
      throwException: true,
    });
  };

  const updateAisle = async (id: number, request: IUpdateAisle) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.updateAisle(id, request);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const deleteAisle = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });

        await EndPoints.wmsLayout.deleteAisle(id);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "delete" } });
      },
      throwException: true,
    });
  };

  const reorderAisle = async (id: number, pickSequence: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
        await EndPoints.wmsLayout.reorderAisle(id, pickSequence);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "update" } });
      },
      throwException: true,
    });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: IAisleQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  return (
    <AisleContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getDetails,
          setDetails,
          createAisle,
          updateAisle,
          deleteAisle,
          reorderAisle,
          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </AisleContext.Provider>
  );
};

export default AisleContextProvider;
